import { FC, useEffect } from "react";

import { CourseType } from "components/server-types";
import { t } from "i18n/i18n";
import { Listbox } from "mds/components/Listbox";
import { ORDER_BY_CODE } from "mds/hooks/use-ordered";
import { TextBooksIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { courseSelectors, selectAdminAccessibleCourses } from "store/selectors";
import { getCourseDisplayTitle } from "utils/course";

export type CourseMenuProps = {
  selectedCourseId: string | null;
  onCourseChange: (courseId: string) => void;
  defaultToAll?: boolean;
  adminAccessibleOnly?: boolean;
  showEmptyOption?: boolean;
  size?: "xs" | "s";
  disabled?: boolean;
  filterBy?: (course: CourseType) => boolean;
  className?: string;
};

// This is very similar to TermMenu, consider refactoring to a shared general dropdown component
export const CourseMenu: FC<CourseMenuProps> = ({
  selectedCourseId,
  onCourseChange,
  defaultToAll = true,
  showEmptyOption = true,
  adminAccessibleOnly = false,
  size = "xs",
  disabled = false,
  filterBy,
  className,
}) => {
  const allCourses = useAppSelector(
    adminAccessibleOnly ? selectAdminAccessibleCourses : courseSelectors.selectAll,
  ).toSorted(ORDER_BY_CODE);
  const courses = filterBy ? allCourses.filter(filterBy) : allCourses;
  const emptyCourseOption: CourseType = {
    id: "",
    code: defaultToAll ? t("import_modal.all_courses") : t("import_modal.select_course"),
    title: "",
    grouping_id: null,
  };
  const courseOptions = showEmptyOption ? [emptyCourseOption, ...courses] : courses;

  useEffect(() => {
    storeApi.courses.list();
  }, []);

  const listboxItems = courseOptions.map((courseOption) => ({
    id: courseOption.id,
    title: getCourseDisplayTitle(courseOption),
    iconStart: <TextBooksIcon />,
  }));

  return (
    <Listbox
      buttonSize={size}
      className={className}
      disabled={disabled}
      items={listboxItems}
      selectedValue={selectedCourseId ?? ""}
      onSelectionChange={(item) => onCourseChange(item.id)}
    />
  );
};
